import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { ApiEndPoint } from '@/services/apiUttiLities';
import { COMPLETE_EVENT } from '@/context/DutyCheckJobContext';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });
const POLL_DUTY_MS = 15000;

// Duty badge for one user, driven by GET /duty-status
export const DutyBadge = ({ status }) => {
  if (status === 'in_duty') return <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">🟢 On duty</span>;
  if (status === 'not_in_duty') return <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-700 border border-red-300">🔴 Off duty</span>;
  if (status === 'pending') return <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-300">⏳ Pending</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-gray-100 text-gray-600 border border-gray-300">⚪ No response</span>;
};

// "This scenario matched before — assign the same personnel?" prompt.
// Shown on a scenario-matched real incident while its plan is under review.
const PersonnelOfferPrompt = ({ incidentId, onApplied, variant = 'light' }) => {
  const dk = variant === 'dark';
  const [offer, setOffer] = useState(null);
  const [duty, setDuty] = useState({});
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchDutyStatus = useCallback(async () => {
    try {
      const dutyRes = await axios.get(`${ApiEndPoint}/api/duty-status`, { headers: authHeaders() });
      setDuty(dutyRes.data?.data?.users || {});
    } catch (e) {
      console.error('duty status load failed', e);
    }
  }, []);

  useEffect(() => {
    if (!incidentId) return;
    (async () => {
      try {
        const offerRes = await axios.get(`${ApiEndPoint}/api/incidents/${incidentId}/personnel-offer`, { headers: authHeaders() });
        setOffer(offerRes.data?.data || null);
      } catch (e) {
        console.error('personnel offer load failed', e);
      }
    })();
    fetchDutyStatus();
  }, [incidentId, fetchDutyStatus]);

  useEffect(() => {
    const refresh = () => { fetchDutyStatus(); };
    window.addEventListener(COMPLETE_EVENT, refresh);
    const interval = setInterval(refresh, POLL_DUTY_MS);
    return () => {
      window.removeEventListener(COMPLETE_EVENT, refresh);
      clearInterval(interval);
    };
  }, [fetchDutyStatus]);

  const roster = offer?.offer || [];
  if (dismissed || offer?.already_applied || roster.length === 0) return null;

  const apply = async (onlyOnDuty) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${ApiEndPoint}/api/incidents/${incidentId}/apply-default-personnel`,
        { only_on_duty: onlyOnDuty },
        { headers: authHeaders() }
      );
      toast.success(`Personnel assigned to ${res.data?.data?.assigned ?? 0} card(s) — review and adjust as needed`);
      setDismissed(true);
      onApplied && onApplied();
    } catch (e) {
      toast.error('Failed to apply personnel');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={dk
        ? 'p-4 rounded-lg border border-cyan-800/70 bg-cyan-950/40'
        : 'mx-4 mt-3 p-4 rounded-xl border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 to-blue-50'}
      data-testid="personnel-offer-prompt"
    >
      <p className={`text-sm font-bold mb-1 ${dk ? 'text-cyan-300' : 'text-indigo-900'}`}>
        👥 This scenario matched before. Assign the same personnel as last time?
      </p>
      <div className="space-y-1.5 my-2">
        {roster.map((entry) => (
          <div key={entry.role} className="flex flex-wrap items-center gap-2 text-xs">
            <span className={`font-semibold ${dk ? 'text-cyan-400' : 'text-indigo-800'}`}>{entry.role}:</span>
            {(entry.users || []).map((u) => (
              <span key={u.userId} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${dk ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-indigo-200 text-gray-800'}`}>
                {u.userName || u.email}
                <DutyBadge status={duty[String(u.userId)]?.status} />
              </span>
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Button size="sm" disabled={busy} className={dk ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'} data-testid="assign-all-btn" onClick={() => apply(false)}>
          Assign all
        </Button>
        <Button size="sm" disabled={busy} variant="outline" className={dk ? 'bg-transparent border-emerald-700 text-emerald-400 hover:bg-emerald-950/60 hover:text-emerald-300' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-50'} data-testid="assign-onduty-btn" onClick={() => apply(true)}>
          Assign on-duty only
        </Button>
        <Button size="sm" variant="ghost" className={dk ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-gray-500'} onClick={() => setDismissed(true)}>
          Skip
        </Button>
      </div>
    </div>
  );
};

export default PersonnelOfferPrompt;
